import { allowedMove, BOARD_DONE_DAYS, BOARD_STATUSES, CLOSING_STATUSES, columnOf, moveProblem, REPORT_BOARD, resolutionOf, stageOf, STAGE_TITLES, type BoardColumn } from '../../../shared/board';
import type { BugReportListItem, BugStatus } from '../bugApi';
import { compareValues, type CellValue, type SortRule } from '@ui';
import { inEpic, type EpicIds } from './EpicFilter';

export interface BoardFilter {
  text: string;
  /** Фл1: the reports of the chosen epic; null — every epic. */
  epicIds: EpicIds;
  /** Пов10: only the reports with a confirmed «повтор» / «дополнение» link or a high suggestion. */
  repeating?: boolean;
  /** Пов5–Пов8: only the reports with this sign (`back`, `unfinished`, `closed`, `stuck`). */
  sign?: string;
}

export const NO_FILTER: BoardFilter = { text: '', epicIds: null, repeating: false };

/** Дк2: a report in a final status last changed more than BOARD_DONE_DAYS whole days ago is not on the board. */
export function onBoard(report: Pick<BugReportListItem, 'status' | 'updatedAt'>, now: number = Date.now()): boolean {
  if (!(CLOSING_STATUSES as readonly string[]).includes(report.status)) return true;
  return Math.floor((now - Date.parse(report.updatedAt)) / 86_400_000) <= BOARD_DONE_DAYS;
}

export interface Lane {
  cells: { column: BoardColumn; cards: BugReportListItem[] }[];
}

/** Дк5, Фл1: a card is shown when it fits the search and the chosen epic. */
export function matches(report: BugReportListItem, filter: BoardFilter): boolean {
  if (!inEpic(report.id, filter.epicIds)) return false;
  if (filter.repeating && !report.repeat) return false;
  if (filter.sign && !(report.signs ?? []).includes(filter.sign)) return false;
  const text = filter.text.trim().toLowerCase();
  if (!text) return true;
  const number = report.number ?? report.id;
  return [`№${number}`, String(number), report.description, report.reporter ?? '', report.project].some((v) => v.toLowerCase().includes(text));
}

/** Пт3: the statuses the board loads — every stage but Backlog and Done. */
export const BOARD_OPEN_STATUSES = BOARD_STATUSES.filter((s) => stageOf(s) !== 'backlog' && stageOf(s) !== 'done');

/** Д1: each report — in exactly the column of its stage. Пр1: the board holds one project, so there is one lane. */
export function layout(reports: BugReportListItem[], filter: BoardFilter): Lane {
  const shown = reports.filter((r) => matches(r, filter));
  return {
    cells: REPORT_BOARD.columns.map((column) => ({
      column,
      cards: shown.filter((r) => columnOf(REPORT_BOARD, r.status)?.id === column.id),
    })),
  };
}

export function columnCount(reports: BugReportListItem[], column: BoardColumn): number {
  return reports.filter((r) => columnOf(REPORT_BOARD, r.status)?.id === column.id).length;
}

/** Дк1: the swimlanes of the final column — one per resolution, in the order of its statuses. */
export function resolutionLanes(column: BoardColumn, cards: BugReportListItem[]): { status: BugStatus; cards: BugReportListItem[] }[] {
  return (column.statuses as BugStatus[]).map((status) => ({ status, cards: cards.filter((c) => c.status === status) }));
}

/** Status a drop into the column sets: the card's own if it is already there, else the column's first. */
export function dropStatus(column: BoardColumn, from: BugStatus): BugStatus {
  return (column.statuses as BugStatus[]).includes(from) ? from : (column.statuses[0] as BugStatus);
}

/** Дк1: a drop to `to` is a move along the stages; the reason when it is not. */
export function dropVerdict(from: BugStatus, to: BugStatus): { ok: boolean; why: string | null } {
  return { ok: allowedMove(from, to), why: moveProblem(from, to) };
}

/** The column can take the card — the others dim while dragging. */
export function canDrop(column: BoardColumn, from: BugStatus): boolean {
  const to = dropStatus(column, from);
  return to !== from && allowedMove(from, to);
}

/** Пт4: the stage word for the full list. */
export function stageLabel(status: BugStatus): string {
  return STAGE_TITLES[stageOf(status)];
}

/** The columns of «Все репорты» that sort (Ср1). */
export type AllColumn = 'number' | 'text' | 'stage' | 'resolution' | 'date';

const STAGE_ORDER = Object.keys(STAGE_TITLES);

/** The list line of a report: its words without the form's labels and shot marks. */
export const firstLine = (r: Pick<BugReportListItem, 'description'>): string =>
  r.description
    .replace(/\[снимок \d+\]/g, ' ')
    .replace(/(Что делали|Что ждали|Причина и решение \(предположение\)|Что произошло):/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || '(без описания)';

/** What a column of «Все репорты» sorts by (Ср1, Рм1: the same values feed `DataTable`). */
export const reportValue = (r: BugReportListItem, column: AllColumn): CellValue => {
  if (column === 'number') return r.number ?? r.id;
  if (column === 'text') return firstLine(r);
  if (column === 'stage') return STAGE_ORDER.indexOf(stageOf(r.status));
  if (column === 'resolution') return resolutionOf(r.status) ?? '';
  return new Date(r.createdAt);
};

/** Ср1: rows by the chosen column — up, then down; no rule keeps the arrival order; equal values keep their order. */
export function sortReports(reports: BugReportListItem[], rule: SortRule | undefined): BugReportListItem[] {
  if (!rule) return reports;
  const sign = rule.direction === 'desc' ? -1 : 1;
  return reports
    .map((report, index) => ({ report, index }))
    .sort((a, b) => sign * compareValues(reportValue(a.report, rule.key as AllColumn), reportValue(b.report, rule.key as AllColumn)) || a.index - b.index)
    .map((x) => x.report);
}
